import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Spinner } from "@/components/ui-kit";
import api from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const request = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.dev_otp) { setOtp(data.dev_otp); toast.info(`Preview code: ${data.dev_otp}`, { duration: 20000 }); }
      if (data.sent) toast.success(`Code sent by SMS to ${data.masked_phone}`);
      else toast.message("Reset code generated. (SMS sender not configured yet — use the preview code shown.)", { duration: 8000 });
      setStep(2);
    } catch (e2) { setErr(e2.response?.data?.detail || "Could not send code"); }
    setLoading(false);
  };

  const reset = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: pw });
      toast.success("Password reset — please sign in with your new password");
      nav("/login");
    } catch (e2) { setErr(e2.response?.data?.detail || "Could not reset password"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "radial-gradient(circle at 15% 20%, rgba(42,82,152,.14), transparent 45%), radial-gradient(circle at 85% 80%, rgba(218,165,32,.12), transparent 45%), linear-gradient(180deg,#f6f8fc,#eef2fa)" }}>
      <div className="w-full max-w-[420px] bg-white rounded-3xl p-9 border border-slate-200" style={{ boxShadow: "0 20px 60px rgba(10,27,51,.16)" }}>
        <div className="flex items-center justify-center mb-2"><Logo height={38} /></div>
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 pq-badge bg-slate-100 text-slate-600 mb-2"><ShieldCheck size={12} /> Password reset</div>
          <h1 className="font-display text-xl font-bold text-slate-900">{step === 1 ? "Reset your password" : "Enter your code"}</h1>
          <p className="text-slate-500 text-[13px]">{step === 1 ? "We'll text a 6-digit code to the phone on your account." : "Enter the code and choose a new password."}</p>
        </div>

        {step === 1 ? (
          <form onSubmit={request}>
            <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Email</span>
              <input required type="email" className="pq-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email" placeholder="you@email.com" /></label>
            {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mt-1" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="reset-request-btn" className="pq-btn pq-btn-primary w-full py-3 mt-3 text-[14.5px]">
              {loading ? <Spinner className="w-4 h-4" /> : <>Send code <ArrowRight size={17} /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={reset}>
            <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">6-digit code</span>
              <input required inputMode="numeric" maxLength={6} className="pq-input tracking-[0.4em] text-center metric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="reset-otp" placeholder="••••••" /></label>
            <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">New password</span>
              <div className="relative">
                <input required type={showPw ? "text" : "password"} className="pq-input pr-10" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password" placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="reset-toggle-pw" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </label>
            {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mt-1" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="pq-btn pq-btn-primary w-full py-3 mt-3 text-[14.5px]">
              {loading ? <Spinner className="w-4 h-4" /> : <>Reset password <ArrowRight size={17} /></>}
            </button>
            <button type="button" onClick={() => { setStep(1); setErr(""); }} className="w-full text-center text-[12.5px] text-slate-500 mt-3 hover:text-slate-700" data-testid="reset-resend">Use a different email / resend</button>
          </form>
        )}
        <Link to="/login" className="flex items-center justify-center gap-1.5 text-[12.5px] text-slate-400 mt-5 hover:text-slate-600"><ArrowLeft size={13} /> Back to sign in</Link>
      </div>
    </div>
  );
}
